import React, { useEffect, useState, useCallback } from 'react';
import { MessageCircle, Send, UserCheck, Bot } from 'lucide-react';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import apiService from '../services/apiService';
import { CHANNEL_TR } from '../i18n/tr';

export default function SocialInbox({ isAdmin }) {
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);

  const loadConversations = useCallback(async () => {
    try {
      const data = await apiService.fetchSocialConversations();
      setConversations(data);
      if (!selectedId && data.length) setSelectedId(data[0].id);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  const loadMessages = useCallback(async (id) => {
    if (!id) return;
    const data = await apiService.fetchSocialMessages(id);
    setMessages(data);
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (selectedId) loadMessages(selectedId);
  }, [selectedId, loadMessages]);

  const selected = conversations.find((c) => c.id === selectedId);

  const handleReply = async () => {
    if (!reply.trim() || !selectedId) return;
    await apiService.replySocialConversation(selectedId, reply.trim());
    setReply('');
    await loadMessages(selectedId);
  };

  const handleTakeover = async () => {
    if (!selectedId || !isAdmin) return;
    await apiService.takeoverSocialConversation(selectedId);
    await loadConversations();
  };

  const handleRelease = async () => {
    if (!selectedId || !isAdmin) return;
    await apiService.releaseSocialConversation(selectedId);
    await loadConversations();
  };

  return (
    <div className="p-6 lg:p-8 h-full flex flex-col">
      <PageHeader
        title="Sosyal Gelen Kutusu"
        subtitle="Facebook ve Instagram DM görüşmeleri"
        icon={MessageCircle}
      />

      {loading ? (
        <p className="text-slate-400">Yükleniyor…</p>
      ) : conversations.length === 0 ? (
        <EmptyState title="Görüşme yok" description="Meta webhook bağlandığında DM'ler burada görünür." />
      ) : (
        <div className="flex flex-1 min-h-0 gap-4 mt-4">
          <div className="w-72 shrink-0 card overflow-auto p-2 space-y-1">
            {conversations.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setSelectedId(c.id)}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm ${
                  selectedId === c.id ? 'bg-accent-500/20 text-white' : 'hover:bg-white/5 text-slate-300'
                }`}
              >
                <div className="font-medium">{c.userName || c.externalUserId}</div>
                <div className="text-xs text-slate-500">
                  {CHANNEL_TR[c.platform] || c.platform}
                  {c.humanTakeover ? ' · Personel' : ' · AI'}
                </div>
              </button>
            ))}
          </div>

          <div className="flex-1 card flex flex-col min-h-0">
            {selected && (
              <div className="border-b border-white/10 px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-white">{selected.userName || selected.externalUserId}</p>
                  <p className="text-xs text-slate-500">{CHANNEL_TR[selected.platform]}</p>
                </div>
                {isAdmin && (
                  <div className="flex gap-2">
                    {selected.humanTakeover ? (
                      <button type="button" onClick={handleRelease} className="btn-secondary text-xs flex items-center gap-1">
                        <Bot className="h-4 w-4" /> AI'ya bırak
                      </button>
                    ) : (
                      <button type="button" onClick={handleTakeover} className="btn-secondary text-xs flex items-center gap-1">
                        <UserCheck className="h-4 w-4" /> Devral
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="flex-1 overflow-auto p-4 space-y-3">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    m.direction === 'inbound'
                      ? 'bg-white/5 text-slate-200'
                      : 'ml-auto bg-accent-500/20 text-white'
                  }`}
                >
                  {m.content}
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 p-3 flex gap-2">
              <input
                className="field flex-1"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Yanıt yazın…"
                onKeyDown={(e) => e.key === 'Enter' && handleReply()}
              />
              <button type="button" onClick={handleReply} className="btn-primary flex items-center gap-1">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
